import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DocCategoriesManager from './documents/DocCategoriesManager';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyForm = {
  title_ru: '',
  title_en: '',
  description_ru: '',
  description_en: '',
  file_url: '',
  category: '',
  is_active: true
};

const DocumentsManagement = () => {
  const [activeTab, setActiveTab] = useState('documents'); // 'documents' | 'categories'
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [docRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/documents`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/document-categories`, { headers: getAuthHeaders() }),
      ]);
      setDocuments(docRes.data || []);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      showNotification('Ошибка загрузки документов', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const catName = (key) => categories.find((c) => c.key === key)?.name_ru || key || '—';

  const handleOpenModal = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setFormData({
        title_ru: doc.title_ru || '',
        title_en: doc.title_en || '',
        description_ru: doc.description_ru || '',
        description_en: doc.description_en || '',
        file_url: doc.file_url || '',
        category: doc.category || '',
        is_active: doc.is_active
      });
    } else {
      setEditingDoc(null);
      setFormData({ ...emptyForm, category: categories[0]?.key || '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingDoc(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDoc) {
        await axios.put(`${API_URL}/api/admin/documents/${editingDoc.id}`, formData, { headers: getAuthHeaders() });
        showNotification('Документ успешно обновлён');
      } else {
        await axios.post(`${API_URL}/api/admin/documents`, formData, { headers: getAuthHeaders() });
        showNotification('Документ успешно создан');
      }
      handleCloseModal();
      loadAll();
    } catch (error) {
      console.error('Error saving document:', error);
      showNotification('Ошибка сохранения документа', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить этот документ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/documents/${id}`, { headers: getAuthHeaders() });
      showNotification('Документ успешно удалён');
      loadAll();
    } catch (error) {
      console.error('Error deleting document:', error);
      showNotification('Ошибка удаления документа', 'error');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Загрузка...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Управление документами</h1>
          <p className="text-gray-600 mt-2">Нормативные акты и внешние ссылки</p>
        </div>
        {activeTab === 'documents' && (
          <button
            onClick={() => handleOpenModal()}
            data-testid="admin-doc-add-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition"
          >
            + Добавить документ
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('documents')}
          data-testid="admin-doc-tab-documents"
          className={`px-4 py-2 font-medium transition border-b-2 -mb-px ${activeTab === 'documents' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Документы ({documents.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          data-testid="admin-doc-tab-categories"
          className={`px-4 py-2 font-medium transition border-b-2 -mb-px ${activeTab === 'categories' ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Категории ({categories.length})
        </button>
      </div>

      {notification && (
        <div className={`p-4 rounded-lg ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {notification.message}
        </div>
      )}

      {activeTab === 'categories' ? (
        <DocCategoriesManager categories={categories} onChanged={loadAll} notify={showNotification} />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Название (RU)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Категория</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">{doc.title_ru}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{catName(doc.category)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${doc.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {doc.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-3">
                    <button onClick={() => handleOpenModal(doc)} className="text-blue-600 hover:text-blue-800">Редактировать</button>
                    <button onClick={() => handleDelete(doc.id)} className="text-red-600 hover:text-red-800">Удалить</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">{editingDoc ? 'Редактировать документ' : 'Создать документ'}</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Название (RU) *</label>
                  <input type="text" name="title_ru" value={formData.title_ru} onChange={handleInputChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Название (EN) *</label>
                  <input type="text" name="title_en" value={formData.title_en} onChange={handleInputChange} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Описание (RU) *</label>
                  <textarea name="description_ru" value={formData.description_ru} onChange={handleInputChange} rows={3} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Описание (EN) *</label>
                  <textarea name="description_en" value={formData.description_en} onChange={handleInputChange} rows={3} required className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Внешняя ссылка на документ (URL) *</label>
                <input type="url" name="file_url" value={formData.file_url} onChange={handleInputChange} required placeholder="https://docs.eaeunion.org/... или http://government.ru/..." className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500" />
                <p className="text-xs text-gray-500 mt-1">Прямая ссылка на нормативный акт (откроется в новой вкладке).</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Категория *</label>
                  <select name="category" value={formData.category} onChange={handleInputChange} required data-testid="admin-doc-category-select" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 bg-white">
                    <option value="" disabled>— Выберите категорию —</option>
                    {[...categories].sort((a, b) => a.order - b.order).map((cat) => (
                      <option key={cat.key} value={cat.key}>{cat.name_ru}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center pt-8">
                  <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded" />
                  <label className="ml-2 block text-sm text-gray-900">Активен</label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={handleCloseModal} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Отмена</button>
                <button type="submit" data-testid="admin-doc-save-btn" className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg">{editingDoc ? 'Сохранить' : 'Создать'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsManagement;
